import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import ReactDOM from 'react-dom';
import { handleBeforeInput } from '../RichEditor/utils';
import { Editor, convertToRaw, EditorState } from 'draft-js';
import { transformTextToEditorState } from '../../utils/dataSync';
import { useStore } from '../../store/store';
import { debounce } from 'lodash';
import { convertMargin, convertPadding, getHeaderStyle } from '../../utils/table';
import ColumnResizer from './ColumnResizer';
import FloatyTextStyling from './FloatyTextStyling';
import { useCellClickOutside } from './useCellClickOutside';
import { useInsertionTargetRegistration } from './insertionTarget';
import { useStyleMap } from '../../hooks';
import EditorWrapper from '../common/EditorWrapper';
import ContextualMenu from '../ContextualMenu/ContextualMenu';
import { MENU_ANCESTORS, viewerTypes } from '../../constants/constants';
import { EditorProvider } from '../../contexts/EditorContext';
import ControlRow from './ControlRow';
import { usePreviewContext } from '../../contexts/PreviewContext';
import { useTabHandler } from '../../hooks';
import { RichUtils } from 'draft-js';
import '../RichEditor/index.css'
import { useStyledPaste } from '../RichEditor/useStyledPaste';
import { mergeConfigurationHighlightColors } from '../../utils/styles';

const Header = ({
  rowSpan,
  colSpan = 1,
  tableIsSelected,
  title,
  boxId,
  box,
  handleClickInTable,
  updateTheadHeight,
  columnKey,
  tableStyle,
  align,
  isLastColumn,
  nextColKey,
  rotation,
  setColumnSizeVars,
  computeMaxColumnWidth,
  drawMode,
  isFirstColumn,
  controlRowsClicked,
  setControlRowsClicked,
  zIndex,
  columnLength,
  placementIndex,
  isParent = false,
  firstRowRef,
  onControlRowClick,
  columnWidthPercent,
  bodyEdgeStyle,
}) => {
  const { fromViewer } = usePreviewContext();
  const styleMap = useStyleMap()
  const setEventManagerEnabled = useStore(({ setEventManagerEnabled }) => setEventManagerEnabled)
  const updateBox = useStore(({ updateBox }) => updateBox)
  const setTableColumnCustomLabel = useStore(({ setTableColumnCustomLabel }) => setTableColumnCustomLabel)
  const setTableColumnAlign = useStore(({ setTableColumnAlign }) => setTableColumnAlign)
  const configuration = useStore(({ configuration }) => configuration)
  const selectedBoxId = useStore(({ selectedBoxId }) => selectedBoxId)

  const [editorState, setEditorState] = useState(transformTextToEditorState(title));
  const [isSelected, setIsSelected] = useState(false)
const [defaultSelection, setDefaultSelection] = useState()
useInsertionTargetRegistration({
  boxId,
  isSelected: tableIsSelected && isSelected,
  editorState,
  setEditorState,
  setDefaultSelection,
});
  const [headerTopOffset, setHeaderTopOffset] = useState(0);
  const consumeSkipBoxSaveOnUnselect = useStore((state) => state.consumeSkipBoxSaveOnUnselect);
  const documentContentSyncTick = useStore((state) => state.documentContentSyncTick);

  useEffect(() => {
    if (!isSelected) {
      setEditorState(transformTextToEditorState(title))
    }
  }, [title, isSelected]);

  useEffect(() => {
    setEditorState(transformTextToEditorState(title));
  }, [documentContentSyncTick, title]);

  const inputRef = useRef();
  const thRef = useRef();

  const style = { ...getHeaderStyle({ tableStyle, configuration, columnKey, isLastColumn }), ...bodyEdgeStyle }
  const parentRowType = "header"
  const clickedRow = controlRowsClicked.find(row => row.parentRowId === columnKey && row.parentRowType === parentRowType)
  const isControlRowClicked = !!clickedRow
  const afterClickedRow = nextColKey ? controlRowsClicked.find(row => row.parentRowId === nextColKey && row.parentRowType === parentRowType) : null
  const isControlRowAfterClicked = !!afterClickedRow
  const isControlRowClickedToDelete = isControlRowClicked && clickedRow?.isToDelete

  const focus = useCallback(() => {
    setIsSelected(true)
    setEventManagerEnabled(false);
    inputRef.current.focus()
  }, [setEventManagerEnabled])

  const handleFocus = (e) => {
    if (tableIsSelected && !isSelected) {
      handleClickInTable({ columnKey, type: 'columns' })(e)
      e.preventDefault();
      focus()
    }
  }

  useEffect(() => {
    if (box.selectedItem?.columnKey === columnKey && box.selectedItem?.type === 'columns' && selectedBoxId === boxId) {
      focus()
    }
  }, [box.selectedItem?.columnKey, box.selectedItem?.type, columnKey, focus, boxId, selectedBoxId, updateBox, box.selectedItem]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const save = useCallback((newEditorState) => {
    updateTheadHeight();
    // Tableau variable : l'en-tête et le Drawer ciblent la MÊME valeur
    // (customLabel de la config). On écrit le plein texte dans la config plutôt
    // que column.title -> les deux restent synchro, et le renommage survit au
    // refetch/reapply (qui recalcule column.title depuis customLabel). Sans
    // libellé d'origine retrouvable, renommage habituel (column.title).
    if (box.tableName) {
      const text = newEditorState.getCurrentContent().getPlainText();
      if (setTableColumnCustomLabel(box.id, columnKey, text)) return;
    }
    updateBox(boxId, (box) => {
      const column = box.content.columns.find((col) => col.key === columnKey)
      if (column)
        column.title = JSON.stringify(convertToRaw(newEditorState.getCurrentContent()));
    });
  }, [updateTheadHeight]);

  // Alignement du seul titre de la colonne : ses cases gardent le leur.
  const alignHeaderText = (value) => {
    const headerTextAlign = { left: 'start', right: 'end' }[value] ?? value;
    updateBox(boxId, (box) => {
      const column = box.content.columns.find((col) => col.key === columnKey);
      if (column) column.headerTextAlign = headerTextAlign;
    });
    // Tableau lu de l'API : la config seule survit à une relecture.
    if (box.tableName) setTableColumnAlign(box.id, columnKey, 'headerAlign', headerTextAlign);
  };

  const { myKeyBindingFn } = useTabHandler(editorState, setEditorState);

  const handleKeyCommand = useCallback(
    (command) => {
      if (command) {
        setEditorState(
          RichUtils.toggleInlineStyle(editorState, command.toUpperCase())
        );
      }
    },
    [editorState]
  );

  const cleanupRef = useRef(editorState);

  useEffect(() => {
    cleanupRef.current = editorState
  });

  useLayoutEffect(() => {
    return () => {
      if (tableIsSelected && isSelected && cleanupRef.current) {
        if (consumeSkipBoxSaveOnUnselect(boxId)) return;
        save(cleanupRef.current);
      }
    };
  }, [tableIsSelected, isSelected, save, boxId, consumeSkipBoxSaveOnUnselect]);

  const { padding, minHeight } = convertPadding(tableStyle.size, tableStyle.customCellPadding, tableStyle.customCellPaddingUnit);
  const { margin } = convertMargin(tableStyle.size);

  const ref = useRef()
  useCellClickOutside({
    ref,
    isSelected,
    onClickOutside: () => {
      save(editorState)
      setIsSelected(false)
    },
  });

  // Collage mis en forme, ou texte seul (raccourci, pastille).
  const highlightColors = useMemo(() => mergeConfigurationHighlightColors(configuration), [configuration]);
  const styledPaste = useStyledPaste({
    editorState,
    write: setEditorState,
    anchorRef: ref,
    active: tableIsSelected && isSelected,
    colors: configuration.colors,
    highlightColors,
    defaultColor: tableStyle.customHeaderTextColor,
    defaultFontSize: style?.fontSize,
  });
  const menuAncestorIds = useMemo(() => [boxId, columnKey], [boxId, columnKey])

  const setParentLineHovered = useCallback(({ isHovered, isLeftButton }) => {
    if (!isHovered) {
      document.querySelectorAll(`[data-col-cell="${columnKey}"]`)
        .forEach(el => el.classList.remove('column-line-hovered', 'is-first-column'))
      return
    }
    document.querySelectorAll(`[data-col-cell="${columnKey}"]`).forEach(el => {
      el.classList.add('column-line-hovered')
      if (isLeftButton) el.classList.add('is-first-column')
      else el.classList.remove('is-first-column')
    })
  }, [columnKey])

  // Mesurer l'offset du th actuel par rapport au premier tr
  useLayoutEffect(() => {
    const measureOffset = () => {
      if (thRef.current && firstRowRef?.current) {
        const thRect = thRef.current.getBoundingClientRect();
        const firstRowRect = firstRowRef.current.getBoundingClientRect();
        // getBoundingClientRect est à l'échelle du zoom du canevas (90 % à
        // « 100 % ») alors que l'offset sert de `top` CSS : sans ce rapport, la
        // règle d'une colonne sous un groupement descendait sur l'en-tête du
        // groupement (hauteur de la ligne × (1 − zoom)).
        const scale = thRect.height / thRef.current.offsetHeight || 1;
        setHeaderTopOffset((thRect.top - firstRowRect.top) / scale);
      }
    };

    measureOffset();

    const resizeObserver = new ResizeObserver(measureOffset);
    if (thRef.current) {
      resizeObserver.observe(thRef.current);
    }
    if (firstRowRef?.current) {
      resizeObserver.observe(firstRowRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [firstRowRef]);

  return (
    <th
      scope="col"
      rowSpan={rowSpan}
      colSpan={colSpan}
      style={style}
      data-col-cell={columnKey}
      onClick={(e) => {
        handleClickInTable({ columnKey, type: 'columns' })(e);
        if (tableIsSelected && !isSelected) focus()
      }}
      onDoubleClick={handleFocus}
      className={`is-header-row
      ${isControlRowClicked ? "table-border-clicked" : ""}
      ${isControlRowClickedToDelete ? "table-border-clicked-to-delete" : ""}
      `}
      ref={(el) => {
        ref.current = el;
        thRef.current = el;
      }}
    >
      {tableIsSelected && fromViewer !== viewerTypes.PREVIEW &&
        <ControlRow
          controlRowsClicked={controlRowsClicked}
          isFirstElement={isFirstColumn}
          isLastElement={isLastColumn}
          setControlRowsClicked={setControlRowsClicked}
          parentRowId={columnKey}
          parentRowType={parentRowType}
          isControlRowClicked={isControlRowClicked}
          isControlRowAfterClicked={isControlRowAfterClicked}
          setParentLineHovered={setParentLineHovered}
          zIndex={zIndex}
          columnLength={columnLength}
          boxId={boxId}
          placementIndex={placementIndex}
          parentKey={columnKey}
          headerTopOffset={headerTopOffset}
          onControlRowClick={onControlRowClick}
          columnWidthPercent={columnWidthPercent}
        />}
      <div style={{
        margin: 0,
        backgroundColor: 'transparent',
        zIndex: tableIsSelected ? 1000 : 'auto',
        border: 'none',
        color: style.color,
        padding,
        fontSize: style?.fontSize,
        fontFamily: style?.fontFamily,
        fontWeight: 'normal',
        minHeight,
        height: '100%',
        lineHeight: 1.5714285714285714,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        width: '100%'
      }}>
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: align === 'left' ? 'flex-start' : align === 'center' ? 'center' : align === 'right' ? 'flex-end' : align === 'justify' ? 'flex-start' : 'flex-start',
          minHeight: 0,
          wordWrap: 'break-word',
          wordBreak: 'normal'
        }}>
          <div
            style={{
              width: '100%',
              overflow: 'hidden'
            }}
            onKeyDown={styledPaste.onKeyDown}
            onKeyUp={styledPaste.onKeyUp}
          >
            <EditorProvider setEditorState={setEditorState} editorState={editorState} readOnly={!isSelected}>
              <EditorWrapper
                boxId={`${boxId}-${columnKey}`}
                editorState={editorState}
                box={{ id: `${boxId}-${columnKey}`, content: { alignment: align } }}
                textStyle={{ positions: { horizontal: align } }}
              >
                <Editor
                  stripPastedStyles={true}
                  handlePastedText={styledPaste.handlePastedText}
                  handleBeforeInput={(chars, newEditorState) => handleBeforeInput(
                    {
                      chars,
                      editorState: newEditorState,
                      setEditorState
                    }
                  )}
                  editorState={editorState}
                  ref={inputRef}
                  onChange={!boxId ? null : ((e) => setEditorState(e))}
                  textAlignment={align === 'justify' ? 'left' : align}
                  readOnly={!isSelected}
                  customStyleMap={styleMap}
                  keyBindingFn={myKeyBindingFn}
                  handleKeyCommand={handleKeyCommand}
                  onBlur={() => {
                    save(editorState);
                    updateBox(boxId, (box) => {
                      box.selectedItem = undefined
                    }
                    );
                  }}
                />
              </EditorWrapper>
            </EditorProvider>
          </div>
        </div>
      </div>
      {tableIsSelected &&
        ReactDOM.createPortal(
          <ContextualMenu
            editorState={editorState}
            setEditorState={setEditorState}
            menuAncestorType={MENU_ANCESTORS.BOX.header}
            menuAncestorIds={menuAncestorIds}
            defaultSelection={defaultSelection}
            setDefaultSelection={setDefaultSelection}
          />,
          document.getElementById('SelectionVariablePortal')
        )}
      {styledPaste.pasteChoice}
      {tableIsSelected && isSelected &&
        ReactDOM.createPortal(
          <FloatyTextStyling
            editorState={editorState}
            setEditorState={setEditorState}
            defaultFontSize={style?.fontSize}
            defaultFontFamily={style?.fontFamily}
            defaultColor={tableStyle.customHeaderTextColor}
            colors={configuration.colors}
            hasAlignement
            boxAlignement={align}
            onAlignmentChange={alignHeaderText}
          />,
          document.getElementById('SelectionVariablePortal')
        )}
      {(!isLastColumn || drawMode) && fromViewer !== viewerTypes.PREVIEW && <ColumnResizer
        boxId={boxId}
        rotation={rotation}
        setColumnSizeVars={setColumnSizeVars}
        columnKey={columnKey}
        nextColKey={nextColKey}
        computeMaxColumnWidth={computeMaxColumnWidth}
        resizeEnabled={isLastColumn ? (drawMode && tableIsSelected) : (!drawMode || tableIsSelected)}
      />}
    </th>
  )
}

export default React.memo(Header)
